/**
 * Exam Service Permission Constants
 * Format: service:action:resource:scope
 *
 * Zanzibar namespace: "exam"
 * Relations: creator, proctor, grader, participant, viewer
 */

export const EXAM_SERVICE = 'exam' as const;

// Zanzibar Relations
export const EXAM_RELATIONS = {
  CREATOR: 'creator',
  PROCTOR: 'proctor',
  GRADER: 'grader',
  PARTICIPANT: 'participant',
  VIEWER: 'viewer',
} as const;

export type ExamRelation = (typeof EXAM_RELATIONS)[keyof typeof EXAM_RELATIONS];

export const EXAM_RELATION_HIERARCHY = [
  {
    relation: EXAM_RELATIONS.CREATOR,
    label: 'Creator',
    description: 'Exam creator (instructor/admin). Full control over exam lifecycle.',
    implies: ['proctor', 'grader', 'viewer'],
  },
  {
    relation: EXAM_RELATIONS.PROCTOR,
    label: 'Proctor',
    description: 'Can monitor exam sessions and manage participants.',
    implies: ['grader', 'viewer'],
  },
  {
    relation: EXAM_RELATIONS.GRADER,
    label: 'Grader',
    description: 'Can grade exam submissions and view results.',
    implies: ['viewer'],
  },
  {
    relation: EXAM_RELATIONS.PARTICIPANT,
    label: 'Participant',
    description: 'Enrolled student. Can take the exam.',
    implies: [],
  },
  {
    relation: EXAM_RELATIONS.VIEWER,
    label: 'Viewer',
    description: 'Can view exam details (read-only, no access to answers).',
    implies: [],
  },
] as const;

// Permission strings (service:action:resource:scope)
export const EXAM_PERMISSIONS = {
  // Exam CRUD
  CREATE_EXAM: 'exam:create:exams:own',
  READ_EXAM: 'exam:read:exams:own',
  READ_ALL_EXAMS: 'exam:read:exams:all',
  UPDATE_EXAM: 'exam:update:exams:own',
  DELETE_EXAM: 'exam:delete:exams:own',
  PUBLISH_EXAM: 'exam:publish:exams:own',

  // Question management
  CREATE_QUESTION: 'exam:create:questions:own',
  READ_QUESTIONS: 'exam:read:questions:own',
  UPDATE_QUESTION: 'exam:update:questions:own',
  DELETE_QUESTION: 'exam:delete:questions:own',

  // Grading
  GRADE_ATTEMPTS: 'exam:grade:attempts:own',
  GRADE_ALL_ATTEMPTS: 'exam:grade:attempts:all',

  // Proctoring
  MONITOR_EXAMS: 'exam:monitor:exams:own',
  MONITOR_ALL_EXAMS: 'exam:monitor:exams:all',

  // Enrollment
  ENROLL_STUDENTS: 'exam:enroll:exams:own',
  MANAGE_ENROLLMENT: 'exam:manage:enrollment:own',

  // Analytics
  VIEW_ANALYTICS: 'exam:read:analytics:own',
  VIEW_ALL_ANALYTICS: 'exam:read:analytics:all',

  // Administration
  MANAGE_SETTINGS: 'exam:manage:settings:all',
  MANAGE_USERS: 'exam:manage:users:all',
  VIEW_AUDIT_LOGS: 'exam:read:audit-logs:all',

  // Wildcard (superadmin)
  SUPERADMIN: '*:*:*:*',
} as const;

export type ExamPermission = (typeof EXAM_PERMISSIONS)[keyof typeof EXAM_PERMISSIONS];

// Grouped for organized display
export const EXAM_PERMISSION_CATEGORIES: Record<string, readonly string[]> = {
  'Exam Management': [
    EXAM_PERMISSIONS.CREATE_EXAM,
    EXAM_PERMISSIONS.READ_EXAM,
    EXAM_PERMISSIONS.READ_ALL_EXAMS,
    EXAM_PERMISSIONS.UPDATE_EXAM,
    EXAM_PERMISSIONS.DELETE_EXAM,
    EXAM_PERMISSIONS.PUBLISH_EXAM,
  ],
  'Questions': [
    EXAM_PERMISSIONS.CREATE_QUESTION,
    EXAM_PERMISSIONS.READ_QUESTIONS,
    EXAM_PERMISSIONS.UPDATE_QUESTION,
    EXAM_PERMISSIONS.DELETE_QUESTION,
  ],
  'Grading': [
    EXAM_PERMISSIONS.GRADE_ATTEMPTS,
    EXAM_PERMISSIONS.GRADE_ALL_ATTEMPTS,
  ],
  'Proctoring': [
    EXAM_PERMISSIONS.MONITOR_EXAMS,
    EXAM_PERMISSIONS.MONITOR_ALL_EXAMS,
  ],
  'Enrollment': [
    EXAM_PERMISSIONS.ENROLL_STUDENTS,
    EXAM_PERMISSIONS.MANAGE_ENROLLMENT,
  ],
  'Analytics': [
    EXAM_PERMISSIONS.VIEW_ANALYTICS,
    EXAM_PERMISSIONS.VIEW_ALL_ANALYTICS,
  ],
  'Administration': [
    EXAM_PERMISSIONS.MANAGE_SETTINGS,
    EXAM_PERMISSIONS.MANAGE_USERS,
    EXAM_PERMISSIONS.VIEW_AUDIT_LOGS,
  ],
};
